import React from "react";
import Lenis from "lenis";
import { Box } from "@mui/material";
import {
  SCROLLBAR_THUMB_COLOR,
  SCROLLBAR_THUMB_HOVER_COLOR,
} from "../../universal/scrollbarStyles";
import {
  LenisContext,
  LenisSyncContext,
  ScrollLockRegisterContext,
} from "./lenisContext";
import { MOBILE_NAV_MEDIA } from "../layout/layoutConstants";

const MOBILE_MQ = `@media ${MOBILE_NAV_MEDIA}`;

// Re-export hooks so client-only call sites can keep a single import path.
export {
  scheduleLenisScrollbarSync,
  useLenis,
  useLenisSyncRef,
  useScrollLock,
} from "./lenisContext";

const MIN_OVERFLOW_PX = 1;
const MIN_SCROLLABLE_RATIO = 0.02;
const SCROLLBAR_WIDTH = "3px";
const SCROLLBAR_MIN_THUMB_HEIGHT = 48;

function hideScrollbarTrack(track, thumb) {
  track.style.opacity = "0";
  track.style.pointerEvents = "none";
  thumb.style.height = "0";
  thumb.style.transform = "translateY(0)";
}

function hasMeaningfulOverflow(lenis, viewport) {
  const limit = lenis.limit;

  if (limit <= MIN_OVERFLOW_PX) {
    return false;
  }

  const scrollableRatio = limit / (limit + viewport);
  return scrollableRatio >= MIN_SCROLLABLE_RATIO;
}

function LenisScrollbar({ lenis, scrollLocked, syncRef }) {
  const trackRef = React.useRef(null);
  const thumbRef = React.useRef(null);
  const isDragging = React.useRef(false);
  const dragStartY = React.useRef(0);
  const dragStartScroll = React.useRef(0);
  const [hovered, setHovered] = React.useState(false);
  const isMobile = React.useMemo(
    () =>
      typeof window !== "undefined" &&
      window.matchMedia(MOBILE_NAV_MEDIA).matches,
    [],
  );
  const [mobileScrollbar, setMobileScrollbar] = React.useState(isMobile);

  React.useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return undefined;
    }

    const media = window.matchMedia(MOBILE_NAV_MEDIA);
    const onChange = () => setMobileScrollbar(media.matches);
    onChange();

    if (typeof media.addEventListener === "function") {
      media.addEventListener("change", onChange);
      return () => media.removeEventListener("change", onChange);
    }

    media.addListener(onChange);
    return () => media.removeListener(onChange);
  }, []);

  React.useEffect(() => {
    if (!lenis) return undefined;

    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!track || !thumb) return undefined;

    const update = () => {
      if (scrollLocked || mobileScrollbar) {
        hideScrollbarTrack(track, thumb);
        return;
      }

      const viewport = window.innerHeight;

      if (!hasMeaningfulOverflow(lenis, viewport)) {
        hideScrollbarTrack(track, thumb);
        return;
      }

      const limit = lenis.limit;
      const scrollable = limit + viewport;
      const ratio = viewport / scrollable;
      const height = Math.max(ratio * viewport, SCROLLBAR_MIN_THUMB_HEIGHT);

      track.style.opacity = "1";
      track.style.pointerEvents = "auto";

      const maxTop = viewport - height;
      const progress = limit > 0 ? lenis.scroll / limit : 0;
      const top = progress * maxTop;

      thumb.style.height = `${height}px`;
      thumb.style.transform = `translateY(${top}px)`;
    };

    const syncScrollbar = () => {
      lenis.resize();
      update();
    };

    if (syncRef) {
      syncRef.current = syncScrollbar;
    }

    syncScrollbar();
    lenis.on("scroll", update);
    window.addEventListener("resize", syncScrollbar);

    let layoutRafId = 0;
    const scheduleLayoutSync = () => {
      cancelAnimationFrame(layoutRafId);
      layoutRafId = requestAnimationFrame(() => {
        syncScrollbar();
      });
    };

    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(scheduleLayoutSync)
        : null;

    resizeObserver?.observe(document.documentElement);
    if (document.body) {
      resizeObserver?.observe(document.body);
    }

    return () => {
      lenis.off("scroll", update);
      window.removeEventListener("resize", syncScrollbar);
      resizeObserver?.disconnect();
      cancelAnimationFrame(layoutRafId);
      if (syncRef) {
        syncRef.current = null;
      }
    };
  }, [lenis, scrollLocked, syncRef, mobileScrollbar]);

  const handleThumbMouseDown = React.useCallback((e) => {
    if (scrollLocked || mobileScrollbar) {
      return;
    }

    e.preventDefault();
    isDragging.current = true;
    dragStartY.current = e.clientY;
    dragStartScroll.current = lenis.scroll;

    const onMouseMove = (moveEvent) => {
      if (!isDragging.current || !lenis) return;

      const viewport = window.innerHeight;
      const limit = lenis.limit;
      const scrollable = limit + viewport;
      const ratio = viewport / scrollable;
      const thumbHeight = Math.max(ratio * viewport, SCROLLBAR_MIN_THUMB_HEIGHT);
      const maxTop = viewport - thumbHeight;

      const deltaY = moveEvent.clientY - dragStartY.current;
      const scrollDelta = (deltaY / maxTop) * limit;
      const newScroll = Math.min(Math.max(dragStartScroll.current + scrollDelta, 0), limit);

      lenis.scrollTo(newScroll, { immediate: true });
    };

    const onMouseUp = () => {
      isDragging.current = false;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [lenis, scrollLocked, mobileScrollbar]);

  return (
    <Box
      ref={trackRef}
      aria-hidden
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "fixed",
        top: 0,
        right: 0,
        width: SCROLLBAR_WIDTH,
        height: "100vh",
        zIndex: 1600,
        pointerEvents: "auto",
        backgroundColor: "transparent",
        opacity: 0,
        transition: "opacity 200ms ease",
        [MOBILE_MQ]: {
          display: "none",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        ref={thumbRef}
        onMouseDown={handleThumbMouseDown}
        sx={{
          position: "absolute",
          top: 0,
          right: 0,
          width: SCROLLBAR_WIDTH,
          borderRadius: "999px",
          backgroundColor: hovered ? SCROLLBAR_THUMB_HOVER_COLOR : SCROLLBAR_THUMB_COLOR,
          border: "1px solid transparent",
          backgroundClip: "padding-box",
          transition: "background-color 180ms ease",
          willChange: "transform",
          "&:active": { cursor: "grabbing" },
        }}
      />
    </Box>
  );
}

/**
 * Client-only: imports ESM `lenis`. Do not import this module from SSR paths;
 * use hooks from `./lenisContext` instead.
 */
export default function LenisProvider({ children }) {
  const [lenis, setLenis] = React.useState(null);
  const [scrollLockCount, setScrollLockCount] = React.useState(0);
  const syncRef = React.useRef(null);

  const registerScrollLock = React.useCallback(() => {
    setScrollLockCount((count) => count + 1);
    return () => {
      setScrollLockCount((count) => Math.max(0, count - 1));
    };
  }, []);

  React.useEffect(() => {
    if (!lenis) {
      return;
    }

    if (scrollLockCount > 0) {
      lenis.stop();
      return;
    }

    lenis.start();
  }, [lenis, scrollLockCount]);

  React.useEffect(() => {
    const instance = new Lenis({
      smoothWheel: false,
      prevent: (node) => Boolean(node?.closest?.("[data-lenis-prevent]")),
    });

    setLenis(instance);

    let rafId = 0;
    function raf(time) {
      instance.raf(time);
      rafId = requestAnimationFrame(raf);
    }

    rafId = requestAnimationFrame(raf);

    return () => {
      cancelAnimationFrame(rafId);
      instance.destroy();
      setLenis(null);
    };
  }, []);

  return (
    <LenisContext.Provider value={lenis}>
      <LenisSyncContext.Provider value={syncRef}>
        <ScrollLockRegisterContext.Provider value={registerScrollLock}>
          {children}
          <LenisScrollbar
            lenis={lenis}
            scrollLocked={scrollLockCount > 0}
            syncRef={syncRef}
          />
        </ScrollLockRegisterContext.Provider>
      </LenisSyncContext.Provider>
    </LenisContext.Provider>
  );
}
